import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../../template/MainTheme/components/Breadcrumbs';
import useTransportationOrderService from '../../../app/services/hooks/useTransportationOrderService';
import useOrderService from '../../../app/services/hooks/useOrderService';
import {
    defaultTransportationOrderDataForm,
    TransportationOrderDataForm
} from '../../../app/models/TransportationOrder';
import LazyLoading from '../../../components/LazyLoading';
import DefaultCard from '../../../components/default/DefaultCard';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import DefaultCreatedModifiedUserForm from '../../../components/default/DefaultCreatedModifiedUserForm';
import { toast } from 'react-toastify';
import TransportationOrderShowForm from '../components/forms/TransportationOrderShowForm';
import TransportationOrderItemsContainer from '../components/TransportationOrderItemsContainer';
import { RouterParams } from '../../../app/shared/interfaces';
import { TransportationOrderStatuses } from '../../../app/shared/enums';
import { TransportationQuoteDetail } from '../../../app/models/TransportationQuoteDetail';
import { TransportationOrderStatus } from '../../../app/models/TransportationOrderStatus';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import TransportationOrderChangeStatusEdit from '../components/TransportationOrderChangeStatusEdit';
import { Tab, Tabs } from 'react-bootstrap';
import TransportationOrderInvoicingShowForm from '../components/forms/TransportationOrderInvoicingForm';
import { TransportationOrderInvoicingShowDataForm } from '../../../app/models/TransportationOrderInvoicing';
import { PaymentMethod } from '../../../app/models/PaymentMethod';
import { QuoteReferenceDataForm } from '../../../app/models/Quote';
import { Unit } from '../../../app/models/Unit';
import { ExchangeRateDate } from '../../../app/models/ExchangeRateDate';

const TransportationOrderEdit = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { id } = useParams<RouterParams>();
    const location = useLocation();
    const isUnifiedMode = location.pathname.startsWith('/pedidos/');

    const breadcrumbs: BreadcrumbsItem[] = [
        {
            name: 'Pedidos',
            url: '/pedidos',
            isActive: false
        },
        {
            name: 'Editar pedido #' + id,
            url: (isUnifiedMode ? '/pedidos/' : '/transportation-orders/') + id + '/edit',
            isActive: true
        }
    ];

    const {
        editTransportationOrder,
        fetchingEditTransportationOrder,
        fetchingShowTransportationOrder,
        canChangeStatusTransportationOrder,
        changeStatusTransportationOrder,
        fetchingChangeStatusTransportationOrder
    } = useTransportationOrderService();

    const { editUnifiedOrder, canChangeStatusUnifiedOrder, changeStatusUnifiedOrder } = useOrderService();

    const editTransportationOrderRef = useRef(editTransportationOrder);

    useEffect(() => {
        editTransportationOrderRef.current = editTransportationOrder;
    }, [editTransportationOrder]);

    const [transportationOrderForm, setTransportationOrderForm] =
        useState<TransportationOrderDataForm>(defaultTransportationOrderDataForm);
    const [quotes, setQuotes] = useState<TransportationQuoteDetail[]>([]);
    const [statuses, setStatuses] = useState<TransportationOrderStatus[]>([]);
    const [transportationOrderInvoicingForm, setTransportationOrderInvoicingForm] =
        useState<TransportationOrderInvoicingShowDataForm>({});
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [references, setReferences] = useState<QuoteReferenceDataForm[]>([]);
    const [exchangeRateDate, setExchangeRateDate] = useState<ExchangeRateDate[]>([]);
    const [quantityUnits, setQuantityUnits] = useState<Unit[]>([]);
    const [orderId, setOrderId] = useState<number | undefined>(undefined);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);

    const reloadModule = useCallback(() => {
        const parsedId = Number(id);
        if (Number.isNaN(parsedId)) {
            return;
        }

        const fetchFn = isUnifiedMode ? editUnifiedOrder : editTransportationOrderRef.current;
        fetchFn(parsedId, {
            onSuccess: (response: ServiceResponse) => {
                setQuotes(response.data.quotes);
                setStatuses(response.data.statuses);
                setTransportationOrderForm(response.data.transportation_order);
                setTransportationOrderInvoicingForm(response.data.invoicing);
                setPaymentMethods(response.data.payment_methods ?? []);
                setReferences(response.data.references ?? []);
                setExchangeRateDate(response.data.exchange_rate_date ?? []);
                setQuantityUnits(response.data.quantity_units ?? []);
                if (response.data.order_id) setOrderId(response.data.order_id);
                setIsLoaded(true);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                setIsLoaded(true);
            }
        });
    }, [id]);

    useEffect(() => {
        reloadModule();
    }, [reloadModule]);

    const requestChangeStatus = (status: string) => {
        if (showLoading) showLoading('loading', 'Cargando...');
        const canChangeStatusFn = isUnifiedMode && orderId
            ? (statusArg: string, events: any) => canChangeStatusUnifiedOrder(orderId, statusArg, events)
            : (statusArg: string, events: any) => canChangeStatusTransportationOrder(parseInt(id), statusArg, events);
        canChangeStatusFn(status, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                changeStatus(status, response.message);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message,
                    message_list: response.data?.message_list ?? []
                });
                toast.error(response.message);
            }
        });
    };

    const changeStatus = (status: string, message: string) => {
        let _text = `Está a punto de cambiar el estado del pedido de transporte #${id}.${message ?? ''}`;

        if(status === TransportationOrderStatuses.ANULADO){
            _text = "Está a punto de anular el pedido de transporte, este cambiará a estado Activo"
        }
        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, cambiar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Cambiando estado...');
                        const changeFn = isUnifiedMode && orderId
                            ? (statusArg: string, events: any) => changeStatusUnifiedOrder(orderId, statusArg, events)
                            : (statusArg: string, events: any) => changeStatusTransportationOrder(Number(id), statusArg, events);
                        changeFn(status, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                reloadModule();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message,
                                    message_list: response.data?.message_list ?? []
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El pedido de transporte no ha cambiado de estado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs
                pageSection={`Editar pedido #${id}`}
                breadcrumbs={breadcrumbs}
            />

               <Tabs defaultActiveKey="general-data" id="quote-tabs" className="mb-3">
                    <Tab eventKey="general-data" title="Datos Generales">
                        <div className="row">
                            <div className="col-md-6">
                                <DefaultCard>
                                    <div className="h3 mt-0 mb-2 card-title">Datos Generales</div>
                                    {isLoaded && !fetchingEditTransportationOrder ? (
                                        <TransportationOrderShowForm
                                            transportationOrderDataForm={transportationOrderForm}
                                            quotes={quotes}
                                        />
                                    ) : (
                                        <LazyLoading />
                                    )}
                                </DefaultCard>
                            </div>
                            <div className="col-md-6">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="h3 mt-0 mb-2 card-title">Usuarios</div>
                                        {isLoaded && !fetchingEditTransportationOrder ? (
                                            <DefaultCreatedModifiedUserForm
                                                createdBy={transportationOrderForm.created_user}
                                                createdAt={transportationOrderForm.created_at}
                                                updatedBy={transportationOrderForm.last_modified_user}
                                                updatedAt={transportationOrderForm.updated_at}
                                                status={transportationOrderForm.status}
                                            />
                                        ) : (
                                            <LazyLoading />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                    </Tab>


                    <Tab eventKey="invoicing" title="Facturación">
                        <DefaultCard>
                            <div className="h3 mt-0 mb-2 card-title">Facturación</div>
                            {isLoaded && !fetchingEditTransportationOrder ? (
                                <TransportationOrderInvoicingShowForm
                                    transportationOrderInvoicingDataForm={transportationOrderInvoicingForm}
                                    setTransportationOrderInvoicingDataForm={setTransportationOrderInvoicingForm}
                                    orderId={orderId ?? parseInt(id)}
                                    paymentMethods={paymentMethods}
                                    references={references}
                                    exchangeRateDate={exchangeRateDate}
                                    units={quantityUnits}
                                    onSaved={reloadModule}
                                />
                            ) : (
                                <LazyLoading />
                            )}
                        </DefaultCard>
                    </Tab>
                    </Tabs>

                    {isLoaded && !fetchingEditTransportationOrder && !fetchingShowTransportationOrder ? (
                        <>
                            <TransportationOrderItemsContainer
                                transportationOrderId={parseInt(id)}
                                orderId={orderId}
                                canBeEdited={false}
                                currencyId={transportationOrderForm.currency_id}
                                transportationOrderForm={transportationOrderForm}
                                setTransportationOrderForm={setTransportationOrderForm}
                                reloadModule={reloadModule}
                            />
                            <TransportationOrderChangeStatusEdit
                                status={String(transportationOrderForm?.status)}
                                statuses={statuses}
                                requestChangeStatus={requestChangeStatus}
                                fetchingChangeStatus={fetchingChangeStatusTransportationOrder}
                            />
                        </>
                    ) : null}
                </>
    );
};

export default TransportationOrderEdit;
